import classNames from 'classnames';
import stylesColors from 'common/styles/styles.colors';
import * as React from 'react';

import styles from './style.module.css';

interface Props extends React.HTMLAttributes<HTMLDivElement> {}

export const StyledTable = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.table, className)}
      />
    );
  },
);

StyledTable.displayName = 'StyledTable';

export const StickyTHead = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.stickyTHead, className)}
      />
    );
  },
);

StickyTHead.displayName = 'StickyTHead';

export const StickyTopTR = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.stickyTopTR, className)}
      />
    );
  },
);

StickyTopTR.displayName = 'StickyTopTR';

export const StickyTopTH = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.stickyTopTH, className)}
      />
    );
  },
);

StickyTopTH.displayName = 'StickyTopTH';

export const StickyTD = React.forwardRef<
  HTMLDivElement,
  Props & { showRightBorder?: boolean; odd?: boolean }
>((props, ref) => {
  const { className, style, showRightBorder, odd, ...rest } = props;
  return (
    <div
      ref={ref}
      {...rest}
      className={classNames(styles.stickyTD, className)}
      style={{
        ...(showRightBorder && {
          borderRight: `2px solid ${stylesColors.skyLighter}`,
        }),
        ...(odd && {
          backgroundColor: stylesColors.primaryLightest,
        }),
        ...style,
      }}
    />
  );
});

StickyTD.displayName = 'StickyTD';

export const Th = React.forwardRef<
  HTMLDivElement,
  Props & { showRightBorder?: boolean }
>((props, ref) => {
  const { className, style, showRightBorder, ...rest } = props;
  return (
    <div
      ref={ref}
      {...rest}
      className={classNames(styles.th, className)}
      style={{
        ...(showRightBorder && {
          borderRight: `0.25px solid ${stylesColors.skyLight}`,
        }),
        ...style,
      }}
    />
  );
});

Th.displayName = 'Th';

export const Td = React.forwardRef<
  HTMLDivElement,
  Props & { showRightBorder?: boolean; odd?: boolean }
>((props, ref) => {
  const { className, style, showRightBorder, odd, ...rest } = props;
  return (
    <div
      ref={ref}
      {...rest}
      className={classNames(styles.td, className)}
      style={{
        ...(showRightBorder && {
          borderRight: `2px solid ${stylesColors.skyLighter}`,
        }),
        ...(odd && {
          background: stylesColors.primaryLightest,
        }),
        ...style,
      }}
    />
  );
});

Td.displayName = 'Td';

export const Tr = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { className, ...rest } = props;
  return (
    <div ref={ref} {...rest} className={classNames(styles.tr, className)} />
  );
});

Tr.displayName = 'Tr';

export const TBody = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { className, ...rest } = props;
  return (
    <div ref={ref} {...rest} className={classNames(styles.tbody, className)} />
  );
});

TBody.displayName = 'TBody';

export const HeaderContainer = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.headerContainer, className)}
      />
    );
  },
);

HeaderContainer.displayName = 'HeaderContainer';

export const ResizeBorder = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.resizeBorder, className)}
      />
    );
  },
);

ResizeBorder.displayName = 'ResizeBorder';

export const CheckedBox = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.checkedBox, className)}
      />
    );
  },
);

CheckedBox.displayName = 'CheckedBox';

export const UncheckedBox = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.uncheckedBox, className)}
      />
    );
  },
);

UncheckedBox.displayName = 'UncheckedBox';

export const IndeterminateBox = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.uncheckedBox, className)}
      />
    );
  },
);

IndeterminateBox.displayName = 'IndeterminateBox';

export const SortContainer = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.sortContainer, className)}
      />
    );
  },
);

SortContainer.displayName = 'SortContainer';

export const NoDataContainer = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.noDataContainer, className)}
      />
    );
  },
);

NoDataContainer.displayName = 'NoDataContainer';

interface SpanTextProps extends React.HTMLAttributes<HTMLSpanElement> {}

export const SpanText = React.forwardRef<HTMLSpanElement, SpanTextProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <span
        ref={ref}
        {...rest}
        className={classNames(styles.spanText, className)}
      />
    );
  },
);

SpanText.displayName = 'SpanText';

export const StyledText = React.forwardRef<HTMLSpanElement, SpanTextProps>(
  (props, ref) => {
    const { className, ...rest } = props;
    return (
      <span
        ref={ref}
        {...rest}
        className={classNames(styles.styledText, className)}
      />
    );
  },
);

StyledText.displayName = 'StyledText';
